import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Truck, ShoppingCart, BarChart3, Smartphone, Globe, Layers, Scan, MessageSquare, CreditCard, MapPin, Users } from "lucide-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

type Status = "in-progress" | "planned" | "considering";

interface RoadmapItem {
  icon: React.ReactNode;
  title: string;
  description: string;
  status: Status;
  eta?: string;
}

interface RoadmapSection {
  title: string;
  subtitle: string;
  items: RoadmapItem[];
}

const STATUS_CONFIG: Record<Status, { label: string; color: string }> = {
  "in-progress": { label: "In Progress", color: "bg-blue-100 text-blue-700 border-blue-200" },
  "planned":     { label: "Planned",     color: "bg-amber-100 text-amber-700 border-amber-200" },
  "considering": { label: "Considering", color: "bg-muted text-muted-foreground border-border" },
};

const SECTIONS: RoadmapSection[] = [
  {
    title: "🚀 Coming Soon",
    subtitle: "Features being actively built right now",
    items: [
      {
        icon: <Truck className="w-5 h-5 text-orange-500" />,
        title: "Logistics Integration",
        description: "Book dispatch riders, track deliveries in real-time, and print waybills directly from RetailPulse. Integrates with Nigerian logistics providers.",
        status: "in-progress",
        eta: "Q3 2026",
      },
      {
        icon: <ShoppingCart className="w-5 h-5 text-violet-500" />,
        title: "E-Commerce Storefront",
        description: "Sell online with a branded storefront powered by your RetailPulse inventory. Accept orders, process payments, and manage fulfilment — all in one place.",
        status: "in-progress",
        eta: "Q3 2026",
      },
      {
        icon: <Scan className="w-5 h-5 text-emerald-500" />,
        title: "Barcode Scanner Support",
        description: "Scan product barcodes at the POS using a USB or Bluetooth scanner, or your phone's camera. Add products to a sale in one scan.",
        status: "in-progress",
        eta: "Q3 2026",
      },
    ],
  },
  {
    title: "📅 On the Roadmap",
    subtitle: "Confirmed features coming in the next 6 months",
    items: [
      {
        icon: <Smartphone className="w-5 h-5 text-blue-500" />,
        title: "Mobile App (iOS & Android)",
        description: "Run your store from your phone. Full POS, inventory management, and daily reports available offline and on-the-go.",
        status: "planned",
        eta: "Q4 2026",
      },
      {
        icon: <MapPin className="w-5 h-5 text-red-500" />,
        title: "Multi-Location Support",
        description: "Manage multiple branches from one account. Transfer stock between locations, compare branch performance, and consolidate reporting.",
        status: "planned",
        eta: "Q4 2026",
      },
      {
        icon: <MessageSquare className="w-5 h-5 text-green-500" />,
        title: "WhatsApp Order Notifications",
        description: "Automatically send order confirmations, payment receipts, and delivery updates to customers via WhatsApp Business API.",
        status: "planned",
        eta: "Q4 2026",
      },
      {
        icon: <CreditCard className="w-5 h-5 text-indigo-500" />,
        title: "Buy Now, Pay Later (BNPL)",
        description: "Let customers split purchases into instalments using Nigerian BNPL providers. Manage repayment schedules within RetailPulse.",
        status: "planned",
        eta: "Q1 2027",
      },
      {
        icon: <Users className="w-5 h-5 text-pink-500" />,
        title: "Supplier Portal",
        description: "Give suppliers access to a limited portal to view purchase orders, confirm deliveries, and submit invoices directly.",
        status: "planned",
        eta: "Q1 2027",
      },
    ],
  },
  {
    title: "💡 Under Consideration",
    subtitle: "Ideas being evaluated — your feedback shapes priority",
    items: [
      {
        icon: <BarChart3 className="w-5 h-5 text-muted-foreground" />,
        title: "AI Sales Forecasting",
        description: "Predict which products will sell out, recommend reorder quantities, and flag slow-moving stock before it becomes dead stock.",
        status: "considering",
      },
      {
        icon: <Globe className="w-5 h-5 text-muted-foreground" />,
        title: "Multi-Currency Support",
        description: "Accept payments in USD, GBP, and EUR for international customers. Automatic exchange rate conversion and reporting.",
        status: "considering",
      },
      {
        icon: <Layers className="w-5 h-5 text-muted-foreground" />,
        title: "Accounting Software Integration",
        description: "Push your P&L, expenses, and sales directly to QuickBooks or Sage for seamless bookkeeping without double-entry.",
        status: "considering",
      },
    ],
  },
];

export default function Roadmap() {
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
        <div className="mb-4">
          <Link href={`${BASE}/`}>
            <Button variant="ghost" size="sm" className="gap-1.5 -ml-2 text-muted-foreground">
              <ArrowLeft className="w-4 h-4" /> Back to Home
            </Button>
          </Link>
        </div>

        <div className="mb-12">
          <h1 className="text-4xl font-bold tracking-tight mb-3">Roadmap</h1>
          <p className="text-muted-foreground text-lg">
            What we're building next for RetailPulse. Updated regularly as priorities evolve.
          </p>
          <div className="flex flex-wrap gap-3 mt-5">
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
              <span key={key} className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${cfg.color}`}>
                {cfg.label}
              </span>
            ))}
          </div>
        </div>

        <div className="space-y-12">
          {SECTIONS.map((section) => (
            <div key={section.title}>
              <div className="mb-6">
                <h2 className="text-xl font-bold mb-1">{section.title}</h2>
                <p className="text-sm text-muted-foreground">{section.subtitle}</p>
              </div>
              <div className="space-y-4">
                {section.items.map((item) => {
                  const statusCfg = STATUS_CONFIG[item.status];
                  return (
                    <div key={item.title} className="border rounded-xl p-5 bg-card hover:border-primary/30 transition-colors">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                          {item.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <h3 className="font-semibold">{item.title}</h3>
                            <Badge className={`text-xs border ${statusCfg.color}`}>
                              {statusCfg.label}
                            </Badge>
                            {item.eta && (
                              <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                                {item.eta}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground leading-relaxed">{item.description}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 border rounded-2xl p-8 bg-primary/5 border-primary/20 text-center">
          <h3 className="font-bold text-lg mb-2">Have a feature request?</h3>
          <p className="text-muted-foreground text-sm mb-5">
            We build what our customers need. Send us your idea on WhatsApp and we'll evaluate it for the roadmap.
          </p>
          <a href="https://wa.me/2349040460813" target="_blank" rel="noopener noreferrer">
            <Button className="gap-2">
              <MessageSquare className="w-4 h-4" /> Suggest a Feature
            </Button>
          </a>
        </div>

        <div className="mt-8 text-center">
          <p className="text-muted-foreground text-sm mb-4">See what we've already shipped</p>
          <Link href={`${BASE}/changelog`}>
            <Button variant="outline">View Changelog →</Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
